import React, { useEffect } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { dictionariesRequest, dictionaryIds } from '@dict';
import { relationshipTypesSel } from '@dict/duck/selectorsTyped';
import { intl } from '@modules/shared/localization';
import { getPreferredName } from '@modules/shared/utils/preferredName';
import { Form, Select } from '@shared/components';

import { FormProps } from '../Form/Form.types';

const { Option } = Select;

interface Props {
  form: NonNullable<FormProps['form']>;
  firstName?: string;
  lastName?: string;
  gender?: string;
  required?: boolean;
  recipientPerson?: string;
  disabled?: boolean;
  floatingLabel?: boolean;
  noMargin?: boolean;
  allowedRelationships?: string[];
}
const { Item: FormItem } = Form;

const RelationshipTypeDropdown: React.FC<Props> = ({
  firstName = '',
  lastName = '',
  gender,
  form,
  allowedRelationships = [],
  recipientPerson,
  required = false,
  disabled = false,
  floatingLabel = true,
  noMargin = false,
}) => {
  const dispatch = useDispatch();
  const relationshipTypes = useSelector(relationshipTypesSel);
  const profileName = getPreferredName({ firstName, lastName });
  const shouldFilterRel = allowedRelationships.length > 0;
  const formmattedRecipientPerson = recipientPerson
    ? ` ${recipientPerson}`
    : '';

  useEffect(() => {
    if (!relationshipTypes.length) {
      dispatch(dictionariesRequest(dictionaryIds.RELATIONSHIP_TYPES));
    }
  }, [relationshipTypes, dispatch]);

  const requiredRule = {
    required: true,
    message: intl.formatMessage({
      id: 'shared.form.error.isRequired',
    }),
  };

  const relationshipTypeOptions = relationshipTypes.reduce<React.ReactNode[]>(
    (options, relationshipType) => {
      const inFilter = shouldFilterRel
        ? allowedRelationships.includes(relationshipType.name)
        : true;
      if (inFilter && [gender, ''].includes(relationshipType.gender)) {
        options.push(
          <Option
            key={`relationship-${relationshipType.id}`}
            value={relationshipType.id}
          >
            {intl
              .formatMessage(
                {
                  id: 'shared.RelationshipTypeDropdown.relationshipOption',
                },
                {
                  relationshipType: relationshipType.name,
                  profileName,
                  recipientPerson: formmattedRecipientPerson,
                },
              )
              .trim()}
          </Option>,
        );
      }
      return options;
    },
    [],
  );

  return (
    <FormItem style={noMargin ? { margin: 0 } : {}}>
      {form.getFieldDecorator('relationshipTypeId', {
        rules: required ? [requiredRule] : [],
        initialValue: undefined,
      })(
        <Select
          floatingLabel={floatingLabel}
          placeholder={intl.formatMessage({
            id: 'shared.RelationshipTypeDropdown.relationshipType',
          })}
          rounded
          disabled={disabled}
        >
          {relationshipTypeOptions}
        </Select>,
      )}
    </FormItem>
  );
};

export default RelationshipTypeDropdown;
